import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {jobDetailService} from '@jobseeker/common-core/services';
import type {AppState} from '@jobseeker/common-core/types';

const NUDGE_MAX_CTC_RUPEES = 500_000; // 5 LPA
const NUDGE_MIN_EXP_YEARS = 10;

const NUDGE_FREQUENCY_DAYS = 3;
const NUDGE_LIFETIME_CAP = 3;

const NUDGE_COUNT_KEY = 'salary_nudge_apply_count';
const NUDGE_LAST_SHOWN_KEY = 'salary_nudge_apply_last_shown';

const startOfDay = (ts: number): number => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const canShowNudge = (): boolean => {
  try {
    const count = parseInt(localStorage.getItem(NUDGE_COUNT_KEY) ?? '0', 10);
    if (count >= NUDGE_LIFETIME_CAP) return false;
    const lastShown = parseInt(
      localStorage.getItem(NUDGE_LAST_SHOWN_KEY) ?? '0',
      10
    );
    if (lastShown) {
      const calendarDaysSince = Math.round(
        (startOfDay(Date.now()) - startOfDay(lastShown)) / 86_400_000
      );
      if (calendarDaysSince < NUDGE_FREQUENCY_DAYS) return false;
    }
  } catch {
    return false;
  }
  return true;
};

export interface SalaryNudgeEligibility {
  eligible: boolean;
  loading: boolean;
  currentSalary: number;
  currentConfidential: number;
  markShown: () => void;
}

export const useSalaryNudgeEligibility = (): SalaryNudgeEligibility => {
  const profileLoading = useSelector(
    (state: AppState) => state.user?.profileLoading
  );
  const currentSalary = useSelector(
    (state: AppState) => state.user?.profile?.currentSalary ?? 0
  );
  const currentConfidential = useSelector(
    (state: AppState) => state.user?.profile?.confidential ?? 0
  );
  const experienceYear = useSelector(
    (state: AppState) => state.user?.profile?.experienceYear ?? 0
  );
  const currentSalaryUpdatedInLast30Days = useSelector(
    (state: AppState) =>
      state.user?.profile?.currentSalaryUpdatedInLast30Days ?? false
  );

  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (profileLoading !== false) return;
    if (checkedRef.current) return;
    checkedRef.current = true;

    const finish = (result: boolean) => {
      setEligible(result);
      setLoading(false);
    };

    try {
      if (!canShowNudge()) {
        finish(false);
        return;
      }

      const profileEligible =
        experienceYear >= NUDGE_MIN_EXP_YEARS &&
        currentSalary > 0 &&
        currentSalary <= NUDGE_MAX_CTC_RUPEES &&
        !currentSalaryUpdatedInLast30Days;

      if (!profileEligible) {
        finish(false);
        return;
      }
    } catch {
      finish(false);
      return;
    }

    jobDetailService
      .getAppliedJobCount({days: 1})
      .then(response => {
        const count = Number(response?.count ?? 0);
        finish(count === 1);
      })
      .catch(() => finish(false));
  }, [profileLoading]);

  const markShown = () => {
    try {
      const count = parseInt(localStorage.getItem(NUDGE_COUNT_KEY) ?? '0', 10);
      localStorage.setItem(NUDGE_COUNT_KEY, String(count + 1));
      localStorage.setItem(NUDGE_LAST_SHOWN_KEY, String(Date.now()));
    } catch {}
  };

  return {
    eligible,
    loading,
    currentSalary,
    currentConfidential,
    markShown,
  };
};
